import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Input } from 'antd'
import intl from 'react-intl-universal'
import SvgIcon from '@/components/svgIcon'
import './index.scss'

const DEFAULT_COOLDOWN = 60
const DEFAULT_STORAGE_KEY = 'verifyCodeExpireAt'

interface VerifyCodeInputProps {
  /** 输入值 */
  value?: string
  /** 输入变更回调 */
  onChange?: (value: string) => void
  /** 发送验证码回调，应返回 Promise */
  onSend?: () => Promise<void>
  /** 最大长度，默认 6 */
  maxLength?: number
  /** 占位文本 */
  placeholder?: string
  /** 前缀图标名，默认 "verifycode" */
  prefixIcon?: string
  /** 冷却时间（秒），默认 60 */
  cooldown?: number
  /** localStorage 键名 */
  storageKey?: string
  /** 输入框 className */
  inputClassName?: string
  /** 是否禁用 */
  disabled?: boolean
}

export default function VerifyCodeInput ({
  value,
  onChange,
  onSend,
  maxLength = 6,
  placeholder = intl.get('mfa.pleaseEnterTheCode'),
  prefixIcon = 'verifycode',
  cooldown = DEFAULT_COOLDOWN,
  storageKey = DEFAULT_STORAGE_KEY,
  inputClassName = 'loginForm-input',
  disabled = false
}: VerifyCodeInputProps) {
  const [remaining, setRemaining] = useState<number>(() => {
    const expireAt = localStorage.getItem(storageKey)
    if (expireAt) {
      return Math.max(0, Math.ceil((+expireAt - Date.now()) / 1000))
    }
    return 0
  })

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const startCountdown = useCallback(() => {
    if (timerRef.current) return
    timerRef.current = setInterval(() => {
      const expireAt = localStorage.getItem(storageKey)
      if (expireAt) {
        const remaining = Math.max(0, Math.ceil((+expireAt - Date.now()) / 1000))
        setRemaining(remaining)
        if (remaining <= 0) {
          localStorage.removeItem(storageKey)
          clearInterval(timerRef.current)
          timerRef.current = null
        }
      } else {
        setRemaining(0)
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }, 200)
  }, [storageKey])

  // 页面加载时恢复倒计时
  useEffect(() => {
    const expireAt = localStorage.getItem(storageKey)
    if (expireAt && +expireAt > Date.now()) {
      startCountdown()
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [startCountdown, storageKey])

  const handleSend = useCallback(() => {
    const expireAt = localStorage.getItem(storageKey)
    if (expireAt && +expireAt > Date.now()) return

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    debounceRef.current = setTimeout(async () => {
      try {
        onSend && (await onSend())
      } catch (e) {
        // 发送失败不启动倒计时
        return
      }

      const newExpireAt = Date.now() + cooldown * 1000
      localStorage.setItem(storageKey, newExpireAt.toString())
      setRemaining(cooldown)
      startCountdown()
    }, 300)
  }, [onSend, cooldown, storageKey, startCountdown])

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange && onChange(e.target.value)
  }, [onChange])

  return (
    <Input
      className={inputClassName}
      placeholder={placeholder}
      maxLength={maxLength}
      value={value}
      onChange={handleChange}
      prefix={<SvgIcon name={prefixIcon} />}
      suffix={
        <div
          onClick={disabled ? undefined : handleSend}
          className={`loginForm-verifycode-suffix${(remaining > 0 || disabled) ? '--disabled' : ''}`}
        >
          {remaining > 0 ? intl.get('login.retryAfterSeconds', { remaining }) : intl.get('login.getVerificationCode')}
        </div>
      }
    />
  )
}
